import { useCallback, useEffect, useState } from "react";
import {
  AppShell,
  Burger,
  MantineProvider,
  Select,
  SimpleGrid,
  Text,
} from "@mantine/core";
import "./DemoResources.scss";
import Header from "../../components/Tax Pro Only/Header/Header";
import Sidebar from "../../components/Sidebar/Sidebar";
import { useDisclosure, useMediaQuery } from "@mantine/hooks";
import { demoOptions, filterDemoLinks } from "./utils";
import demoLinksData from "./demoLinks.json";
import DemoCard from "./DemoCard/DemoCard";
import DemoTabs from "./DemoTabs/DemoTabs";
import DemoHeader from "./DemoHeader/DemoHeader";
import { imgCDN } from "@/constants";
import { fetchWebinarRecordings } from "@api/resources";
import { notifications, Notifications } from "@mantine/notifications";

interface DemoLink {
  title: string;
  link: string;
  image: string;
  type: string;
  time: string;
}

const demoLinks: DemoLink[] = demoLinksData as DemoLink[];

const DemoResources = () => {
  const [recordings, setRecordings] = useState([]);
  const [loading, setLoading] = useState(true);

  const fetchRecordings = useCallback(async () => {
    try {
      const data = await fetchWebinarRecordings();
      setRecordings(data);
    } catch (err) {
      console.error("Error fetching webinar recordings:", err);
      notifications.show({
        title: "Error Occurred",
        message: "Could not fetch webinar recordings.",
        color: "red",
      });
    } finally {
      setLoading(!loading);
    }
  }, []);

  useEffect(() => {
    fetchRecordings();
  }, [fetchRecordings]);

  const [opened, { toggle }] = useDisclosure();
  const isMobile = useMediaQuery("(max-width: 850px)");
  const [selectedOption, setSelectedOption] = useState<string>("all-resources");
  const [filteredLinks, setFilteredLinks] = useState<DemoLink[]>(demoLinks);

  useEffect(() => {
    setFilteredLinks(filterDemoLinks(selectedOption));
  }, [selectedOption]);

  return (
    <MantineProvider>
      <Notifications />
      <AppShell
        className="home home-page"
        header={{ height: 87 }}
        navbar={{
          width: "15.375rem",
          breakpoint: "sm",
          collapsed: { mobile: !opened },
        }}
        padding="md"
      >
        <AppShell.Header className="app-header">
          <Burger
            className="burger"
            opened={opened}
            onClick={toggle}
            hiddenFrom="sm"
            size="sm"
          />
          <Header />
        </AppShell.Header>
        <AppShell.Navbar className="nav">
          <Sidebar />
        </AppShell.Navbar>
        <AppShell.Main className="main-container">
          <div className="main-content">
            <div className="title-container">
              <h2 className="title">Resources</h2>
              <Text c="dimmed">
                Explore our extensive collection of tutorials and guides
                designed to help you get the most out of our product.
              </Text>
            </div>
            <DemoHeader />

            {isMobile ? (
              <div className="demo-tabs">
                <Select
                  data={demoOptions}
                  value={selectedOption}
                  onChange={(value) => setSelectedOption(value || "")}
                  placeholder="Select a resource"
                  size="lg"
                  mt="lg"
                />
                <SimpleGrid cols={1} spacing="lg" mt="xl">
                  {filteredLinks.map((demo) => (
                    <DemoCard
                      key={demo.link}
                      demoLink={demo.link}
                      demoTitle={demo.title}
                      demoTime={demo.time}
                      demoImage={`${imgCDN}${demo.image}`}
                    />
                  ))}
                </SimpleGrid>
              </div>
            ) : (
              <div className="demo-tabs">
                <DemoTabs recordings={recordings as any} />
              </div>
            )}
          </div>
        </AppShell.Main>
      </AppShell>
    </MantineProvider>
  );
};

export default DemoResources;
